import React, { useState, useEffect } from 'react';
import { X, Smartphone, CreditCard, Building2, Shield, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';

// Razorpay-style checkout modal (MOCKED - no real gateway call)
export default function RazorpayCheckout({ open, onClose, amount, description = 'EZLOAD Pro Subscription', prefill = {}, onSuccess }) {
  const [step, setStep] = useState('select'); // select, upi, card, netbanking, processing, success
  const [method, setMethod] = useState('upi');
  const [upiId, setUpiId] = useState(prefill.upi || '');
  const [card, setCard] = useState({ number: '', name: '', expiry: '', cvv: '' });
  const [bank, setBank] = useState('HDFC');
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (open) { setStep('select'); setMethod('upi'); }
  }, [open]);

  const gst = Math.round(amount * 0.18);
  const total = amount + gst;

  const process = () => {
    setStep('processing');
    let n = 3; setCountdown(n);
    const iv = setInterval(() => { n--; setCountdown(n); if (n <= 0) { clearInterval(iv); setStep('success'); } }, 800);
  };

  const done = () => {
    const paymentId = `pay_${Math.random().toString(36).slice(2, 14).toUpperCase()}`;
    const orderId = `order_${Math.random().toString(36).slice(2, 12).toUpperCase()}`;
    onSuccess && onSuccess({ payment_id: paymentId, order_id: orderId, amount: total, method });
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-fade-in-up">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0B2545] to-[#13315C] p-4 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-white/15 flex items-center justify-center font-bold text-sm">R</div>
            <div>
              <p className="text-sm font-bold leading-tight">Razorpay <span className="text-[10px] font-normal opacity-70">(Test Mode)</span></p>
              <p className="text-[11px] text-white/70 leading-tight">EZLOAD Technologies Pvt. Ltd.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Amount */}
        <div className="px-5 py-4 bg-[#F7FAF9] border-b border-gray-100">
          <p className="text-xs text-gray-500">{description}</p>
          <p className="text-2xl font-extrabold text-[#0B2545] font-[Poppins]">₹ {total.toLocaleString('en-IN')}</p>
          <p className="text-[11px] text-gray-500">Includes ₹{gst} GST (18%)</p>
        </div>

        {/* Content */}
        <div className="p-5">
          {step === 'select' && (
            <>
              <p className="text-sm font-semibold text-[#0B2545] mb-3">Choose payment method</p>
              <RadioGroup value={method} onValueChange={setMethod} className="space-y-2">
                {[
                  { v: 'upi', label: 'UPI', desc: 'Pay via any UPI app — GPay, PhonePe, Paytm', Icon: Smartphone, tag: 'Instant' },
                  { v: 'card', label: 'Card', desc: 'Debit / Credit — Visa, Master, RuPay', Icon: CreditCard, tag: 'Secure' },
                  { v: 'netbanking', label: 'Net Banking', desc: 'All major Indian banks', Icon: Building2, tag: null },
                ].map(o => (
                  <label key={o.v} htmlFor={o.v} className={`flex items-center gap-3 border rounded-xl p-3 cursor-pointer transition-all ${method === o.v ? 'border-[#00A651] bg-[#00A651]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                    <RadioGroupItem value={o.v} id={o.v} />
                    <o.Icon className="w-5 h-5 text-[#0B2545]" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#0B2545]">{o.label}</p>
                      <p className="text-[11px] text-gray-500">{o.desc}</p>
                    </div>
                    {o.tag && <span className="text-[10px] bg-[#00A651]/10 text-[#00A651] px-2 py-0.5 rounded">{o.tag}</span>}
                  </label>
                ))}
              </RadioGroup>

              {method === 'upi' && (
                <div className="mt-4">
                  <Label className="text-xs">UPI ID</Label>
                  <Input placeholder="yourname@paytm" className="mt-1" value={upiId} onChange={e => setUpiId(e.target.value)} />
                  <p className="text-[10px] text-gray-500 mt-1">You'll get a payment request on your UPI app.</p>
                </div>
              )}
              {method === 'card' && (
                <div className="mt-4 space-y-2">
                  <div>
                    <Label className="text-xs">Card Number</Label>
                    <Input placeholder="1234 5678 9012 3456" maxLength={19} className="mt-1 font-mono" value={card.number} onChange={e => setCard({ ...card, number: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Expiry</Label><Input placeholder="MM/YY" maxLength={5} className="mt-1" value={card.expiry} onChange={e => setCard({ ...card, expiry: e.target.value })} /></div>
                    <div><Label className="text-xs">CVV</Label><Input placeholder="123" maxLength={4} type="password" className="mt-1" value={card.cvv} onChange={e => setCard({ ...card, cvv: e.target.value })} /></div>
                  </div>
                  <div><Label className="text-xs">Name on Card</Label><Input placeholder="RAJESH KUMAR" className="mt-1" value={card.name} onChange={e => setCard({ ...card, name: e.target.value })} /></div>
                </div>
              )}
              {method === 'netbanking' && (
                <div className="mt-4">
                  <Label className="text-xs">Select Bank</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {['HDFC', 'ICICI', 'SBI', 'Axis', 'Kotak', 'PNB'].map(b => (
                      <button key={b} type="button" onClick={() => setBank(b)} className={`p-2 border rounded-lg text-xs font-semibold ${bank === b ? 'border-[#00A651] bg-[#00A651]/5 text-[#00A651]' : 'border-gray-200 text-gray-700'}`}>{b}</button>
                    ))}
                  </div>
                </div>
              )}

              <Button onClick={process} className="w-full mt-5 h-11 bg-[#00A651] hover:bg-[#008a43] text-white">Pay ₹{total.toLocaleString('en-IN')}</Button>
              <div className="flex items-center justify-center gap-1 mt-3 text-[10px] text-gray-500"><Shield className="w-3 h-3" /> Secured by 256-bit encryption • PCI DSS Compliant</div>
            </>
          )}

          {step === 'processing' && (
            <div className="py-8 text-center">
              <Loader2 className="w-14 h-14 text-[#00A651] animate-spin mx-auto mb-4" />
              <p className="font-semibold text-[#0B2545] mb-1">Processing payment...</p>
              <p className="text-xs text-gray-500">{method === 'upi' ? 'Approve the request in your UPI app' : method === 'card' ? 'Verifying card details' : 'Redirecting to your bank'}</p>
              <p className="text-xs text-gray-400 mt-2">{countdown}s</p>
            </div>
          )}

          {step === 'success' && (
            <div className="py-6 text-center">
              <div className="w-16 h-16 rounded-full bg-[#00A651]/10 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-10 h-10 text-[#00A651]" />
              </div>
              <p className="font-bold text-[#0B2545] text-lg mb-1">Payment Successful!</p>
              <p className="text-sm text-gray-500">₹{total.toLocaleString('en-IN')} paid via {method === 'upi' ? 'UPI' : method === 'card' ? 'Card' : bank + ' Net Banking'}</p>
              <div className="bg-gray-50 rounded-lg p-3 mt-4 text-left">
                <p className="text-[10px] text-gray-500">Payment ID</p>
                <p className="font-mono text-xs text-[#0B2545]">pay_{Math.random().toString(36).slice(2, 14).toUpperCase()}</p>
              </div>
              <Button onClick={done} className="w-full mt-4 h-11 bg-[#00A651] hover:bg-[#008a43] text-white">Done</Button>
            </div>
          )}
        </div>

        <div className="px-5 py-2 border-t border-gray-100 text-[10px] text-gray-400 text-center">This is a demo of Razorpay checkout — no real charge is made.</div>
      </div>
    </div>
  );
}
